import { Spinner } from '../components/ui/Spinner';
import { useAuth } from './AuthProvider';
import styles from './SignInScreen.module.css';

const HELPDESK = 'helpdesk@exigotech.com.au';

export function SignInScreen(): React.JSX.Element {
  const { status, errorMessage, signIn } = useAuth();
  const loading = status === 'loading';

  return (
    <div className={styles.wrap}>
      <section className={styles.hero}>
        <Circle />
        <Hexagon className={`${styles.decor} ${styles.hexTop}`} />
        <NestedHexagons className={`${styles.decor} ${styles.hexBottom}`} />

        <div className={styles.logoPlate}>
          <img src="/logo.svg" alt="Exigo Tech" className={styles.logoImg} />
        </div>

        <div className={styles.heroBody}>
          <p className={styles.heroEyebrow}>MCI Engagement Finder</p>
          <h1 className={styles.heroHeadline}>
            Know exactly what every deal can claim.
          </h1>
          <p className={styles.heroDesc}>
            A single tool for Microsoft Commerce Incentives — enter a customer&rsquo;s Azure
            consumption, seats and pre-qualification answers, and see which engagements qualify,
            what each one pays, and which gate is holding the rest back.
          </p>
        </div>

        <p className={styles.heroFooter}>
          © {new Date().getFullYear()} Exigo Tech Pty Ltd. All rights reserved.
        </p>
      </section>

      <section className={styles.panel}>
        <div className={styles.panelInner}>
          <h2 className={styles.title}>Welcome to MCI Engagement Finder</h2>
          <p className={styles.body}>
            Sign in with your Microsoft work account to size a deal against the current
            financial-year incentives guide — every payout calculated server-side, from the
            published rules.
          </p>

          <button type="button" className={styles.msButton} onClick={signIn} disabled={loading}>
            {loading ? <Spinner size={18} /> : <MicrosoftLogo />}
            {loading ? 'Signing in…' : 'Sign in with Microsoft'}
          </button>

          <div className={styles.divider}>
            <span className={styles.dividerLine} />
            <span className={styles.dividerLabel}>SINGLE SIGN-ON</span>
            <span className={styles.dividerLine} />
          </div>

          <p className={styles.help}>
            Access is provisioned by your Exigo Tech administrator. If you can&rsquo;t sign in,
            contact <a href={`mailto:${HELPDESK}`}>{HELPDESK}</a>.
          </p>

          {errorMessage && <div className={styles.error}>{errorMessage}</div>}

          <div className={styles.footer}>
            <a href={`mailto:${HELPDESK}?subject=MCI%20Engagement%20Finder%20support`}>Help</a>
            <span className={styles.footerDot} aria-hidden="true">
              ·
            </span>
            <span className={styles.footerMark}>
              Exigo Tech · Internal · Microsoft Confidential
            </span>
          </div>
        </div>
      </section>
    </div>
  );
}

/** The oversized, very faint circle behind the upper-right of the hero panel. */
function Circle(): React.JSX.Element {
  return (
    <svg
      className={`${styles.decor} ${styles.circle}`}
      viewBox="0 0 100 100"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="50" cy="50" r="49" stroke="#ffffff" strokeWidth="0.35" />
    </svg>
  );
}

/** A pointy-top hexagon outline. */
function Hexagon({ className }: { className: string }): React.JSX.Element {
  return (
    <svg className={className} viewBox="0 0 100 116" fill="none" aria-hidden="true">
      <polygon points="50,2 97,30 97,86 50,114 3,86 3,30" stroke="#ffffff" strokeWidth="1" />
    </svg>
  );
}

/** Two concentric hexagons, anchoring the bottom-left corner of the hero panel. */
function NestedHexagons({ className }: { className: string }): React.JSX.Element {
  return (
    <svg className={className} viewBox="0 0 100 116" fill="none" aria-hidden="true">
      <polygon points="50,2 97,30 97,86 50,114 3,86 3,30" stroke="#ffffff" strokeWidth="0.8" />
      <polygon
        points="50,26 76,41 76,73 50,88 24,73 24,41"
        stroke="#ffffff"
        strokeWidth="0.8"
        opacity="0.75"
      />
    </svg>
  );
}

/** The standard four-colour Microsoft mark. */
function MicrosoftLogo(): React.JSX.Element {
  return (
    <svg width="19" height="19" viewBox="0 0 21 21" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}
